/**
 * Membresías Stripe — mirror de api/lib/canonicalVipMembership.ts
 *
 * Modelo:
 * 1. FALLBACK exterior — Exclusive Member prod_UtMbRadxYB3kMP / price_1TtZs… $39.99/mo
 * 2. VARIANTES — exterior SIEMPRE usa el price_ canónico (mismo para todas)
 * 3. CUPONES — promo 50% primer mes (promo_1Txbe…) encima del price_
 */

export type CanonicalMembershipKey = "standard" | "vip";

export type MembershipPriceSource = "variant" | "canonical_fallback" | "none";

export type CanonicalMembershipProduct = {
  key: CanonicalMembershipKey;
  planTiers: readonly string[];
  productName: string;
  productId: string;
  priceId: string;
  priceUsd: number;
  amountCents: number;
};

export const CANONICAL_MEMBERSHIP_PRODUCTS: Record<
  CanonicalMembershipKey,
  CanonicalMembershipProduct
> = {
  standard: {
    key: "standard",
    planTiers: ["standard", "basic"],
    productName: "Standard Member",
    productId: "prod_Uq2hJPiq6iQrsq",
    priceId: "price_1TqMcTH8xCanmtKXtXMQ0SxH",
    priceUsd: 9.99,
    amountCents: 999,
  },
  // Exterior (intl DualCTA / invitacion-premium): un solo product+price para
  // TODAS las variants. Promo 50% primer mes vía CANONICAL_VIP_FIRST_MONTH_PROMO_ID.
  vip: {
    key: "vip",
    planTiers: ["vip"],
    productName: "Exclusive Member",
    productId: "prod_UtMbRadxYB3kMP",
    priceId: "price_1TtZsFH8xCanmtKXoYfNp4MT",
    priceUsd: 39.99,
    amountCents: 3999,
  },
} as const;

export const CANONICAL_VIP_STRIPE_PRODUCT_ID = CANONICAL_MEMBERSHIP_PRODUCTS.vip.productId;
export const CANONICAL_VIP_STRIPE_PRICE_ID = CANONICAL_MEMBERSHIP_PRODUCTS.vip.priceId;
export const CANONICAL_VIP_PRICE_USD = CANONICAL_MEMBERSHIP_PRODUCTS.vip.priceUsd;
export const CANONICAL_VIP_AMOUNT_CENTS = CANONICAL_MEMBERSHIP_PRODUCTS.vip.amountCents;
export const CANONICAL_VIP_PRODUCT_NAME = CANONICAL_MEMBERSHIP_PRODUCTS.vip.productName;

/**
 * Stripe Promotion Code — 50% off first month only (exterior VIP).
 * Applied as discounts[0][promotion_code] in create-subscription-checkout.
 */
export const CANONICAL_VIP_FIRST_MONTH_PROMO_ID = "promo_1TxbeEH8xCanmtKX3xur5bpt";
/** Percent off that the promo represents (for UI copy / originalAmount). */
export const CANONICAL_VIP_FIRST_MONTH_DISCOUNT_PERCENT = 50;

export const CANONICAL_STANDARD_STRIPE_PRODUCT_ID =
  CANONICAL_MEMBERSHIP_PRODUCTS.standard.productId;
export const CANONICAL_STANDARD_STRIPE_PRICE_ID =
  CANONICAL_MEMBERSHIP_PRODUCTS.standard.priceId;
export const CANONICAL_STANDARD_PRICE_USD = CANONICAL_MEMBERSHIP_PRODUCTS.standard.priceUsd;
export const CANONICAL_STANDARD_AMOUNT_CENTS =
  CANONICAL_MEMBERSHIP_PRODUCTS.standard.amountCents;
export const CANONICAL_STANDARD_PRODUCT_NAME =
  CANONICAL_MEMBERSHIP_PRODUCTS.standard.productName;

export function getCanonicalMembershipForPlanTier(
  planTier: string | null | undefined,
): CanonicalMembershipProduct | null {
  if (!planTier) return null;
  const tier = planTier.trim().toLowerCase();
  for (const entry of Object.values(CANONICAL_MEMBERSHIP_PRODUCTS)) {
    if (entry.planTiers.includes(tier)) return entry;
  }
  return null;
}

export function isKnownMembershipPriceId(priceId: string | null | undefined): boolean {
  if (!priceId?.startsWith("price_")) return false;
  const id = priceId.trim();
  return Object.values(CANONICAL_MEMBERSHIP_PRODUCTS).some((e) => e.priceId === id);
}

/**
 * Resolve checkout price_:
 * 1. variant (explicit price_ ≠ baseline)
 * 2. canonical_fallback (baseline for plan_tier)
 */
export function resolveClientMembershipPrice(opts: {
  planTier?: string | null;
  explicitPriceId?: string | null;
}): {
  priceId: string | null;
  source: MembershipPriceSource;
  productKey?: CanonicalMembershipKey;
  isVariant: boolean;
  amountCents: number | null;
  priceUsd: number | null;
  productName: string | null;
} {
  const explicit = opts.explicitPriceId?.trim().startsWith("price_")
    ? opts.explicitPriceId.trim()
    : null;
  const entry = getCanonicalMembershipForPlanTier(opts.planTier);
  const baselineId = entry?.priceId ?? null;

  if (explicit) {
    if (baselineId && explicit === baselineId) {
      return {
        priceId: explicit,
        source: "canonical_fallback",
        productKey: entry!.key,
        isVariant: false,
        amountCents: entry!.amountCents,
        priceUsd: entry!.priceUsd,
        productName: entry!.productName,
      };
    }
    return {
      priceId: explicit,
      source: "variant",
      productKey: entry?.key,
      isVariant: true,
      // Variant amount unknown without admin full_price_usd — caller may override
      amountCents: entry?.amountCents ?? null,
      priceUsd: entry?.priceUsd ?? null,
      productName: entry?.productName ?? null,
    };
  }

  if (entry) {
    return {
      priceId: entry.priceId,
      source: "canonical_fallback",
      productKey: entry.key,
      isVariant: false,
      amountCents: entry.amountCents,
      priceUsd: entry.priceUsd,
      productName: entry.productName,
    };
  }

  return {
    priceId: null,
    source: "none",
    isVariant: false,
    amountCents: null,
    priceUsd: null,
    productName: null,
  };
}

/** @deprecated prefer resolveClientMembershipPrice */
export function resolveClientMembershipStripePriceId(opts: {
  planTier?: string | null;
  explicitPriceId?: string | null;
}): string | null {
  return resolveClientMembershipPrice(opts).priceId;
}

export function getCanonicalVipPlanFallback(): {
  fullPriceUsd: number;
  stripePriceId: string;
  stripeProductId: string;
  productName: string;
} {
  const vip = CANONICAL_MEMBERSHIP_PRODUCTS.vip;
  return {
    fullPriceUsd: vip.priceUsd,
    stripePriceId: vip.priceId,
    stripeProductId: vip.productId,
    productName: vip.productName,
  };
}

export function getCanonicalStandardPlanFallback(): {
  fullPriceUsd: number;
  stripePriceId: string;
  stripeProductId: string;
  productName: string;
} {
  const std = CANONICAL_MEMBERSHIP_PRODUCTS.standard;
  return {
    fullPriceUsd: std.priceUsd,
    stripePriceId: std.priceId,
    stripeProductId: std.productId,
    productName: std.productName,
  };
}

/** For admin price pickers — baseline products always listed first. */
export function listCanonicalMembershipPrices(): Array<{
  id: string;
  label: string;
  tier: string;
  amountUsd: number;
}> {
  return Object.values(CANONICAL_MEMBERSHIP_PRODUCTS).map((e) => ({
    id: e.priceId,
    label: `${e.productName} · $${e.priceUsd}/mo (fallback)`,
    tier: e.key,
    amountUsd: e.priceUsd,
  }));
}
